import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import PanelCard from '../../performance-operations/components/PanelCard';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import ReminderDrawer from '../../performance-reminders/ReminderDrawer';
import { Badge, Sub, Table } from '../../performance-operations/styles';
import {
  fetchBreakdown, formatNumber, metaOfBreakdown, metricOf, usePanel,
} from '../../performance-operations/config';
import {
  TAB_TASKS, assigneeReminderCriteria, processHealthHref,
} from '../../../components/performance/processHealthLinks';
import { TOP_ASSIGNEES, isEmployeeAssignee } from '../config';

// Spec §7.2 — Geciken Sorumlular: assignee kırılımı (yalnız İK), gecikene göre azalan ilk 10. Sütunlar: sorumlu, ünvan, rol, geciken
// görev, en eski gecikme günü, geçerli vekil (hücre ek alanlarından, §3.2). Satır işlemleri (§19/11): çalışan sorumluda Hatırlat
// (bu sorumlunun geciken zorunlu görevleri + ortak süzgeçler) ve Görevleri Gör; İK havuzu / harici satırında yalnız Görevleri Gör
// (sorumlu türüyle); atanmamış ve "Diğer" satırlarında işlem yok.
export function assigneeName(t, cell) {
  switch (cell.key) {
    case 'hr_pool': return t('performance.operations.assignee.hrPool');
    case 'external': return t('performance.operations.assignee.external');
    case 'unassigned': return t('performance.operations.unassigned');
    case 'other': return t('performance.operations.other');
    default: return cell.label || '-';
  }
}

export default function OverdueAssigneesPanel({ filters, onMeta, refreshKey, onChanged }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [reminder, setReminder] = useState(null);
  const key = JSON.stringify(filters);
  const panel = usePanel(() => fetchBreakdown(filters, ['M04'], 'assignee'), [key, refreshKey], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('overdueAssignees', metaOfBreakdown(panel.data)); }, [panel.data, onMeta]);

  const rows = (panel.data?.cells || []).filter((c) => c.key && Number(metricOf(c.metrics, 'M04')?.value || 0) > 0).slice(0, TOP_ASSIGNEES);
  const tasksOf = (cell) => (isEmployeeAssignee(cell.key)
    ? { assigneeEmployeeId: cell.key, overdue: 'true' }
    : { assigneeKind: cell.key, overdue: 'true' });
  const actionsOf = (cell) => {
    if (cell.key === 'unassigned' || cell.key === 'other') return [];
    const view = { type: 'view', label: t('performance.processHealth.rowAction.viewTasks'), onClick: () => history.push(processHealthHref(filters, { tab: TAB_TASKS, task: tasksOf(cell) })) };
    if (!isEmployeeAssignee(cell.key)) return [view];
    return [
      // Seçim tıklamada bir kez kurulur: her çizimde yeni nesne ReminderDrawer önizlemesini yeniden gönderirdi (Plan 3 Önemli 3).
      { type: 'send', label: t('performance.operations.rowAction.remind'), onClick: () => setReminder({ periodId: filters.periodId, criteria: assigneeReminderCriteria(filters, cell.key) }) },
      view,
    ];
  };

  return (
    <PanelCard
      panelKey="overdueAssignees" title={t('performance.processHealth.panel.overdueAssignees')} span={5}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0}
      emptyText={t('performance.operations.panel.noOverdue')}
    >
      <Table>
        <colgroup>
          <col style={{ width: '36%' }} /><col style={{ width: '16%' }} /><col style={{ width: '14%' }} /><col style={{ width: '16%' }} />
          <col style={{ width: '12%' }} /><col style={{ width: '6%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>{t('performance.operations.col.assignee')}</th>
            <th>{t('performance.processHealth.col.role')}</th>
            <th>{t('performance.operations.col.overdue')}</th>
            <th>{t('performance.processHealth.col.oldest')}</th>
            <th>{t('performance.processHealth.col.delegate')}</th>
            <th aria-label={t('performance.operations.col.actions')} />
          </tr>
        </thead>
        <tbody>
          {rows.map((cell) => {
            const extra = cell.assignee || {};
            const actions = actionsOf(cell);
            return (
              <tr key={cell.key} data-row={cell.key}>
                <td title={assigneeName(t, cell)}>
                  <div>{assigneeName(t, cell)}</div>
                  {extra.assigneeTitle ? <Sub>{extra.assigneeTitle}</Sub> : null}
                </td>
                <td>{extra.assigneeRole ? t(`performance.reminder.role.${extra.assigneeRole}`, { defaultValue: extra.assigneeRole }) : '-'}</td>
                <td>{formatNumber(metricOf(cell.metrics, 'M04')?.value, lang, 0)}</td>
                <td>{extra.oldestOverdueDays ? <Badge $tone="danger">{t('performance.operations.days', { count: extra.oldestOverdueDays })}</Badge> : '-'}</td>
                <td title={extra.delegateName || ''}>{extra.delegateName || '-'}</td>
                <td>{actions.length ? <OptionsMenu row={{ id: cell.key }} actions={actions} /> : null}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <ReminderDrawer
        open={!!reminder}
        selection={reminder}
        onClose={() => setReminder(null)}
        onSent={() => { setReminder(null); onChanged?.(); }}
      />
    </PanelCard>
  );
}
